import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NotificationsContext = createContext(null);
const POLL_MS = 12000;

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const seenRef = useRef(new Set());
  const wsRef = useRef(null);
  const swRegRef = useRef(null);
  const [pushOn, setPushOn] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);

  const bling = useCallback((n) => {
    const fn = n.level === "urgent" ? toast.error : n.level === "warning" ? toast.warning : toast.info;
    // Notificările importante (flux/urgent/avertisment) rămân pe ecran până sunt închise
    // de utilizator (după ce a trimis mai departe / a escaladat), nu dispar automat.
    const persistent = n.level === "urgent" || n.level === "warning" || n.type === "workflow";
    fn(`🔔 ${n.title}`, { description: n.body, duration: persistent ? Infinity : 12000, closeButton: true });
    if ("Notification" in window && Notification.permission === "granted") {
      try { new Notification(`HGS-OPEP · ${n.title}`, { body: n.body, tag: n.id, requireInteraction: persistent }); } catch {}
    }
  }, []);

  const ingest = useCallback((list, isInitial) => {
    for (const n of list) {
      if (!seenRef.current.has(n.id)) {
        seenRef.current.add(n.id);
        if (!isInitial) bling(n);
      }
    }
    setItems(list);
    setUnread(list.filter((n) => !n.read).length);
  }, [bling]);

  const handleIncoming = useCallback((n) => {
    if (seenRef.current.has(n.id)) return;
    seenRef.current.add(n.id);
    setItems((prev) => [n, ...prev]);
    setUnread((u) => u + 1);
    bling(n);
  }, [bling]);

  const refresh = useCallback((isInitial = false) => {
    api.get("/notifications").then((r) => { ingest(r.data, isInitial); setLastCheck(Date.now()); }).catch(() => {});
  }, [ingest]);

  useEffect(() => {
    if (!user) return;
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").then((reg) => {
        swRegRef.current = reg;
        reg.pushManager.getSubscription().then((s) => setPushOn(!!s)).catch(() => {});
      }).catch(() => {});
    }
    refresh(true);
    const interval = setInterval(() => refresh(false), POLL_MS);

    // best-effort websocket (falls back silently to polling if ingress blocks WS)
    let ws;
    try {
      const token = localStorage.getItem("hgs_token");
      const wsUrl = API.replace(/^http/, "ws").replace("/api", "") + `/api/ws/notifications?token=${token}`;
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.event === "notification") handleIncoming(msg.data);
        } catch {}
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    } catch {}

    return () => {
      clearInterval(interval);
      if (ws) { try { ws.close(); } catch {} }
    };
  }, [user, refresh, handleIncoming]);

  const markRead = async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    await api.post(`/notifications/${id}/read`).catch(() => {});
  };
  const markAll = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
    await api.post("/notifications/read-all").catch(() => {});
  };

  const urlB64ToUint8 = (b64) => {
    const pad = "=".repeat((4 - (b64.length % 4)) % 4);
    const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
  };

  const enablePush = useCallback(async () => {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        toast.error("Dispozitivul nu suportă notificări push. Pe iPhone: adaugă întâi aplicația pe ecranul principal.");
        return false;
      }
      const { data } = await api.get("/push/public-key");
      if (!data.enabled || !data.key) { toast.error("Notificările push nu sunt configurate pe server."); return false; }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Permisiune refuzată pentru notificări."); return false; }
      const reg = swRegRef.current || (await navigator.serviceWorker.ready);
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8(data.key),
        });
      }
      await api.post("/push/subscribe", { subscription: sub.toJSON() });
      setPushOn(true);
      toast.success("Notificări activate pe acest dispozitiv! 🔔");
      return true;
    } catch (e) {
      toast.error("Nu am putut activa notificările pe acest dispozitiv.");
      return false;
    }
  }, []);

  const disablePush = useCallback(async () => {
    try {
      const reg = swRegRef.current || (await navigator.serviceWorker.ready);
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
        await sub.unsubscribe().catch(() => {});
      }
      setPushOn(false);
      toast.success("Notificări dezactivate pe acest dispozitiv.");
    } catch (e) { toast.error("Nu am putut dezactiva notificările."); }
  }, []);

  return (
    <NotificationsContext.Provider value={{ items, unread, markRead, markAll, refresh, enablePush, disablePush, pushOn, lastCheck }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationsContext);
